import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Breadcrumb from '../../components/Breadcrumb';
import Footer from '../../components/footer';
import { usePermissions } from '../../hooks/usePermissions';

interface SyncLogEntry {
  id: string;
  start_time: any;
  end_time: any;
  duration_ms: number;
  total_fetched: number;
  created: number;
  updated: number;
  skipped: number;
  deactivated: number;
  errors: number;
  agencies_created: number;
  agents_created: number;
  dry_run: boolean;
  timestamp: any;
}

interface PendingItem {
  id: string;
  nombre: string;
  tipo: 'agencia' | 'agente';
  fecha: any;
  mls_key?: string;
}

export default function MLSSyncDashboard() {
  const { user, isAdmin } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [syncLogs, setSyncLogs] = useState<SyncLogEntry[]>([]);
  const [lastSync, setLastSync] = useState<SyncLogEntry | null>(null);
  const [stats, setStats] = useState({
    totalMls: 0,
    activeMls: 0,
    inactiveMls: 0,
  });
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);

  useEffect(() => {
    if (!isAdmin()) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const logsSnap = await getDocs(
          query(collection(db, 'mls_sync_log'), orderBy('timestamp', 'desc'), limit(10))
        );
        const logs = logsSnap.docs.map(d => ({ id: d.id, ...d.data() } as SyncLogEntry));
        setSyncLogs(logs);
        if (logs.length > 0) setLastSync(logs[0]);

        const mlsSnap = await getDocs(
          query(collection(db, 'propiedades'), where('fuente', '==', 'flexmls'))
        );
        let active = 0;
        let inactive = 0;
        mlsSnap.forEach(d => {
          if (d.data().activo) active++;
          else inactive++;
        });
        setStats({ totalMls: mlsSnap.size, activeMls: active, inactiveMls: inactive });

        const pendingAgencies = await getDocs(
          query(collection(db, 'inmobiliaria'), where('verificacion_pendiente', '==', true))
        );
        const pendingAgents = await getDocs(
          query(collection(db, 'asesor'), where('verificacion_pendiente', '==', true))
        );

        const items: PendingItem[] = [];
        pendingAgencies.forEach(d => {
          const data = d.data();
          items.push({ id: d.id, nombre: data.nombre || 'Sin nombre', tipo: 'agencia', fecha: data.fecha_creacion, mls_key: data.mls_office_key });
        });
        pendingAgents.forEach(d => {
          const data = d.data();
          items.push({ id: d.id, nombre: data.nombre || 'Sin nombre', tipo: 'agente', fecha: data.fecha_registro, mls_key: data.mls_agent_key });
        });
        setPendingItems(items);
      } catch (error) {
        console.error('Error loading MLS sync data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (ms: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <UserNav nombreUsuario={user?.nombre} nombreInmobiliaria={user?.inmobiliaria_nombre} />

      <section className="py-4">
        <div className="container-fluid px-4">
          <Breadcrumb items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Sincronizacion MLS' }
          ]} />
          <h2 className="mb-4"><i className="fa-solid fa-rotate me-2 text-primary"></i>Sincronizacion MLS</h2>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="mt-2 text-muted">Cargando datos de sincronizaci&oacute;n...</p>
                </div>
              ) : (
                <>
                  {/* Status Card */}
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0">
                          <i className="fa-solid fa-rotate me-2 text-primary"></i>
                          Estado de Sincronizaci&oacute;n
                        </h5>
                        {lastSync && (
                          <span className={`badge ${lastSync.errors > 0 ? 'bg-warning' : 'bg-success'}`}>
                            {lastSync.errors > 0 ? 'Con errores' : 'OK'}
                          </span>
                        )}
                      </div>

                      {lastSync ? (
                        <div className="row text-center">
                          <div className="col-6 col-md-3 mb-2">
                            <small className="text-muted d-block">&Uacute;ltima sync</small>
                            <strong>{formatDate(lastSync.timestamp)}</strong>
                          </div>
                          <div className="col-6 col-md-3 mb-2">
                            <small className="text-muted d-block">Duraci&oacute;n</small>
                            <strong>{formatDuration(lastSync.duration_ms)}</strong>
                          </div>
                          <div className="col-6 col-md-3 mb-2">
                            <small className="text-muted d-block">Procesadas</small>
                            <strong>{lastSync.total_fetched}</strong>
                          </div>
                          <div className="col-6 col-md-3 mb-2">
                            <small className="text-muted d-block">Errores</small>
                            <strong className={lastSync.errors > 0 ? 'text-danger' : 'text-success'}>
                              {lastSync.errors}
                            </strong>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No hay registros de sincronizaci&oacute;n a&uacute;n.</p>
                      )}
                    </div>
                  </div>

                  {/* Stats Cards */}
                  <div className="row mb-4">
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm text-center">
                        <div className="card-body">
                          <i className="fa-solid fa-house fa-2x text-primary mb-2"></i>
                          <h3 className="mb-0">{stats.totalMls}</h3>
                          <small className="text-muted">Propiedades MLS Total</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm text-center">
                        <div className="card-body">
                          <i className="fa-solid fa-circle-check fa-2x text-success mb-2"></i>
                          <h3 className="mb-0">{stats.activeMls}</h3>
                          <small className="text-muted">Activas</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <div className="card border-0 shadow-sm text-center">
                        <div className="card-body">
                          <i className="fa-solid fa-circle-pause fa-2x text-warning mb-2"></i>
                          <h3 className="mb-0">{stats.inactiveMls}</h3>
                          <small className="text-muted">Desactivadas</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Last Sync Details */}
                  {lastSync && (
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body">
                        <h5 className="card-title mb-3">
                          <i className="fa-solid fa-chart-bar me-2 text-info"></i>
                          Detalle &Uacute;ltima Sync
                        </h5>
                        <div className="row">
                          <div className="col-6 col-md-3 mb-2">
                            <div className="p-2 bg-light rounded text-center">
                              <small className="text-muted d-block">Creadas</small>
                              <strong className="text-success">{lastSync.created}</strong>
                            </div>
                          </div>
                          <div className="col-6 col-md-3 mb-2">
                            <div className="p-2 bg-light rounded text-center">
                              <small className="text-muted d-block">Actualizadas</small>
                              <strong className="text-primary">{lastSync.updated}</strong>
                            </div>
                          </div>
                          <div className="col-6 col-md-3 mb-2">
                            <div className="p-2 bg-light rounded text-center">
                              <small className="text-muted d-block">Sin cambios</small>
                              <strong className="text-muted">{lastSync.skipped}</strong>
                            </div>
                          </div>
                          <div className="col-6 col-md-3 mb-2">
                            <div className="p-2 bg-light rounded text-center">
                              <small className="text-muted d-block">Desactivadas</small>
                              <strong className="text-warning">{lastSync.deactivated}</strong>
                            </div>
                          </div>
                        </div>
                        {(lastSync.agencies_created > 0 || lastSync.agents_created > 0) && (
                          <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                            <small>
                              <i className="fa-solid fa-wand-magic-sparkles me-1"></i>
                              Auto-creados: {lastSync.agencies_created} agencias, {lastSync.agents_created} agentes
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pending Verifications */}
                  {pendingItems.length > 0 && (
                    <div className="card border-0 shadow-sm mb-4 border-start border-warning border-3">
                      <div className="card-body">
                        <h5 className="card-title mb-3">
                          <i className="fa-solid fa-triangle-exclamation me-2 text-warning"></i>
                          Pendientes de Verificar ({pendingItems.length})
                        </h5>
                        <div className="table-responsive">
                          <table aria-label="Tabla de Mls Sync Dashboard" className="table table-sm table-hover mb-0">
                            <thead>
                              <tr>
                                <th scope="col">Tipo</th>
                                <th scope="col">Nombre</th>
                                <th scope="col">Clave MLS</th>
                                <th scope="col">Fecha</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pendingItems.slice(0, 20).map(item => (
                                <tr key={item.id}>
                                  <td>
                                    <span className={`badge ${item.tipo === 'agencia' ? 'bg-info' : 'bg-secondary'}`}>
                                      {item.tipo === 'agencia' ? 'Agencia' : 'Agente'}
                                    </span>
                                  </td>
                                  <td>{item.nombre}</td>
                                  <td><code>{item.mls_key || '-'}</code></td>
                                  <td>{formatDate(item.fecha)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {pendingItems.length > 20 && (
                            <small className="text-muted">
                              Mostrando 20 de {pendingItems.length} pendientes
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sync History */}
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                      <h5 className="card-title mb-3">
                        <i className="fa-solid fa-clock-rotate-left me-2 text-secondary"></i>
                        Historial de Sincronizaciones
                      </h5>
                      {syncLogs.length === 0 ? (
                        <p className="text-muted mb-0">No hay registros a&uacute;n.</p>
                      ) : (
                        <div className="table-responsive">
                          <table aria-label="Tabla de Mls Sync Dashboard" className="table table-sm table-hover mb-0">
                            <thead>
                              <tr>
                                <th scope="col">Fecha</th>
                                <th scope="col">Duraci&oacute;n</th>
                                <th scope="col">Procesadas</th>
                                <th scope="col">Creadas</th>
                                <th scope="col">Actualizadas</th>
                                <th scope="col">Desactivadas</th>
                                <th scope="col">Errores</th>
                                <th scope="col">Modo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {syncLogs.map(log => (
                                <tr key={log.id}>
                                  <td>{formatDate(log.timestamp)}</td>
                                  <td>{formatDuration(log.duration_ms)}</td>
                                  <td>{log.total_fetched}</td>
                                  <td className="text-success">{log.created}</td>
                                  <td className="text-primary">{log.updated}</td>
                                  <td className="text-warning">{log.deactivated}</td>
                                  <td className={log.errors > 0 ? 'text-danger fw-bold' : ''}>
                                    {log.errors}
                                  </td>
                                  <td>
                                    <span className={`badge ${log.dry_run ? 'bg-secondary' : 'bg-success'}`}>
                                      {log.dry_run ? 'Dry Run' : 'Live'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                      <h5 className="card-title mb-3">
                        <i className="fa-solid fa-terminal me-2"></i>
                        Comandos
                      </h5>
                      <div className="bg-dark text-light p-3 rounded" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        <div className="mb-1"><span className="text-success">$</span> npm run mls:sync:dry &nbsp;<span className="text-muted"># Prueba sin escribir a Firestore</span></div>
                        <div className="mb-1"><span className="text-success">$</span> npm run mls:sync &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-muted"># Sincronizaci&oacute;n manual</span></div>
                        <div><span className="text-success">$</span> npm run mls:sync:schedule <span className="text-muted"># Cron cada {6} horas</span></div>
                      </div>
                    </div>
                  </div>
                </>
              )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
